import { Box, Button, List, ListItemButton, Popover, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight as ArrowRightIcon, ArrowDropUp as ArrowDropUpIcon } from '@mui/icons-material/';
import { useErrorBoundary } from 'react-error-boundary';
import TagsService from '../../services/tags.service';
import classes from './TagsDropdown.module.scss';
import Tags from '../../views/tags/Tags';
import { setTags as setTagFilters } from '../../../store/filters/filters.slice';
import { Tag } from '../../interfaces/Tag';
import StringHelper from '../../helpers/string.helper';
import { TagTypes } from '../../enums/TagTypes';
import { RootState } from '../../../store';
import { Content } from '../../enums/Content';
import { setTipsTags } from '../../../store/filters/tipFilters.slice';

const MOST_POPULAR_TAGS_LIMIT: number = 25;

const TagsDropdown = ({ content, tagValues, setTagValues }: { content: Content, tagValues?: Tag[], setTagValues?: (tags: Tag[]) => void }) => {
  const { showBoundary } = useErrorBoundary();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagTypes, setTagTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [popularTags, setPopularTags] = useState<Tag[]>([]);
  const [searchTagInput, setSearchTagInput] = useState<string>('');
  const currentFilterTags = useSelector((state: RootState) =>
    content === Content.TIPS
      ? state.tipFilters.tipFilters.tags ?? []
      : state.filters.filters.tags ?? []
  );
  const dispatch = useDispatch();

  useEffect(() => {
    const abortController = new AbortController();
    TagsService.getAllTypes()
      .then((data) => setTagTypes(data))
      .catch((error: Error) => showBoundary(error));

    return () => abortController.abort();
  }, [showBoundary]);

  useEffect(() => {
    if (tagValues && setTagValues) {
      setSelectedTags(tagValues);
    } else if (content && !open) {
      setSelectedTags(currentFilterTags);
    }
  }, [currentFilterTags, tagValues, setTagValues, content, open]);

  useEffect(() => {
    const abortController = new AbortController();

    if (searchTagInput.length === 0) {
      setTags([]);
    }

    if (searchTagInput.length > 1 && selectedType) {
      TagsService.getByNameAndType(searchTagInput, selectedType)
        .then((tags) => {
          setTags(tags);
        })
        .catch((error) => showBoundary(error));
    }

    return () => abortController.abort();
  }, [searchTagInput, selectedType, showBoundary]);

  const onTagDeleteHandle = (tag: Tag) => {
    setSelectedTags(previousSelectedTags => previousSelectedTags.filter(selectedTag => selectedTag !== tag));
  };

  const clearButtonHandler = () => {
    setSelectedTags([]);
  };

  const applyButtonHandler = useCallback(() => {
    if (tagValues && setTagValues) {
      setTagValues(selectedTags);
    } else if (content) {
      dispatch(
        content === Content.TIPS ? setTipsTags(selectedTags) : setTagFilters(selectedTags)
      );
    }

    setAnchorEl(null);
  }, [selectedTags, content, dispatch, tagValues, setTagValues]);

  const typeClickHandler = (tagType: string) => {
    setTags([]);
    setPopularTags([]);
    setSearchTagInput('');
    setSelectedType(tagType);
    TagsService.getMostPopularTagsByType(tagType)
      .then((data) => {
        setPopularTags(data.slice(0, MOST_POPULAR_TAGS_LIMIT))
      })
      .catch((error) => showBoundary(error));
  };

  const renderTags = useMemo(() => {
    const tagClickHandler = (tag: Tag) => {
      const doesTagExists = selectedTags.some((selectedTag) => selectedTag.id === tag.id);

      if (!doesTagExists) {
        setSelectedTags(prevSelectedTags => prevSelectedTags.concat(tag));
      }
    };

    if (searchTagInput.length > 0) {
      return <Tags tags={tags} onTagClick={tagClickHandler} />;
    }

    if (selectedType) {
      return <>
        <Typography className='h5 my-2'>Most popular {selectedType} tags</Typography>
        <Tags tags={popularTags} onTagClick={tagClickHandler} />
      </>;
    }

    return null;
  }, [tags, selectedTags, popularTags, searchTagInput, selectedType]);

  const tagButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Box>
        <Button
          className='filter-button'
          onClick={tagButtonClickHandler}
          endIcon={open ? <ArrowDropUpIcon /> : <ArrowRightIcon />}
        >
          Tags
        </Button>
      </Box>
      <Popover
        className='my-2 ms-2'
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box className={`${classes['dropdown']} d-flex flex-column`}>
          <Box className={`${classes['tags-box']} d-flex flex-column flex-sm-row border-bottom border-1`}>
            <Box className={classes['search-box']}>
              <Typography className={classes['search-text']} variant='h3' gutterBottom >
                Search by Tags
              </Typography>
              <Typography className='mb-2' variant='body1' >
                Select the topic (tags) to personalize your content experience on homepage or narrow down your hunt. You can select as many as
                you would like.
              </Typography>
              <Typography className={classes['all-types-text']} variant='h5' >
                All Types
              </Typography>
              <List className={`${classes['list']} scrollbar`} >
                {tagTypes
                  .filter(tagType => TagTypes.VENUE !== tagType)
                  .map((tagType) => (
                    <ListItemButton
                      disableGutters
                      selected={selectedType === tagType}
                      key={StringHelper.generateRandomString(10)}
                      onClick={() => typeClickHandler(tagType)}
                    >
                      <Typography variant='subtitle2' className={classes['types-text']}>
                        {tagType === TagTypes.TEMPLATE ? 'competition' : tagType}
                      </Typography>
                    </ListItemButton>
                  ))
                }
              </List>
            </Box>
            <Box className={classes['types-box']}>
              {selectedType && (
                <>
                  <Typography className={classes['selected-text']} textTransform='capitalize' variant='h3' >
                    {selectedType}
                  </Typography>
                  <TextField className='my-3' value={searchTagInput} variant='standard' color='success' placeholder={`Search ${selectedType} tags`} onChange={(event) => setSearchTagInput(event.target.value)} />
                  <Box className={`${classes['selected-types-tags']} scrollbar`} >
                    {renderTags}
                  </Box>
                </>
              )}
            </Box>
            <Box className={`${classes['preferences-box']} scrollbar`}>
              <Typography variant='h3' className={classes['preferences-text']} gutterBottom >
                Selected tags
              </Typography>
              <Tags tags={selectedTags} onDelete={onTagDeleteHandle} lightColored />
            </Box>
          </Box>
          <Box className='d-flex justify-content-end align-items-center py-3'>
            <Button size='large' variant='contained' onClick={clearButtonHandler} className={`${classes['clear-button']}`}>
              Clear All
            </Button>
            <Button size='large' variant='contained' onClick={applyButtonHandler} className={classes['apply-button']}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default TagsDropdown;
